import type { ActionDefinition } from '../../types';
import type { ActionHandler, ActionContext } from '../../registry/ActionRegistry';

/**
 * Click action definition
 */
export interface ClickAction extends ActionDefinition {
  type: 'click';
  targetSelector: string;
  /** Show ripple animation at click location */
  showRipple?: boolean;
  /** Wait before clicking (ms) */
  delayBefore?: number;
  /** Timeout for finding element (ms) */
  timeout?: number;
}

/**
 * Handler for click actions
 * Finds element, optionally shows ripple, then clicks
 */
export class ClickActionHandler implements ActionHandler<ClickAction> {
  async execute(action: ClickAction, context: ActionContext): Promise<void> {
    const { targetSelector, showRipple = false, delayBefore = 0, timeout = 5000 } = action;

    // Wait before action if specified
    if (delayBefore > 0) {
      await context.timerAdapter.delay(delayBefore);
    }

    // Find element with timeout
    const element = await context.domAdapter.waitForElement(targetSelector, timeout);
    if (!element) {
      throw new Error(`Element not found: ${targetSelector}`);
    }

    // Check if element is visible
    if (!context.domAdapter.isVisible(element)) {
      throw new Error(`Element not visible: ${targetSelector}`);
    }

    // Check if element is disabled
    if (context.domAdapter.isDisabled(element)) {
      throw new Error(`Element is disabled: ${targetSelector}`);
    }

    // Show ripple animation if requested
    if (showRipple) {
      await context.animationAdapter.rippleEffect(element, 'rgba(59, 130, 246, 0.4)');
    }

    // Trigger click
    context.domAdapter.click(element);

    // Brief delay to allow click to propagate
    await context.timerAdapter.delay(50);
  }

  validate(action: ClickAction): boolean {
    return Boolean(action.targetSelector);
  }
}
